import { useState, useEffect } from 'react';
import { onSnapshot, query, collection, orderBy, addDoc, deleteDoc, doc, serverTimestamp } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { db, storage } from '../config/firebase';

export const useGalerie = () => {
  const [photos, setPhotos] = useState([]);
  const [albums, setAlbums] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);

  // Charger les photos en temps réel
  useEffect(() => {
    const q = query(collection(db, 'galerie'), orderBy('date', 'desc'));
    
    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const data = snapshot.docs.map(doc => {
          const photoData = doc.data();
          return {
            id: doc.id,
            ...photoData,
            date: photoData.date.toDate ? photoData.date.toDate() : new Date(photoData.date)
          };
        });
        setPhotos(data);
        
        // Extraire les albums uniques
        const uniqueAlbums = [...new Set(data.map(p => p.album).filter(Boolean))];
        setAlbums(uniqueAlbums);
        
        setLoading(false);
        setError(null);
      },
      (err) => {
        console.error('Erreur galerie:', err);
        setError(err.message);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, []);

  // Upload une photo
  const uploadPhoto = async (file, data) => {
    try {
      setUploadProgress(10);
      
      // Upload vers Firebase Storage
      const fileName = `${Date.now()}_${file.name.replace(/[^a-zA-Z0-9.-]/g, '_')}`;
      const storageRef = ref(storage, `galerie/${fileName}`);
      
      setUploadProgress(30);
      const snapshot = await uploadBytes(storageRef, file, {
        contentType: file.type
      });
      
      setUploadProgress(70);
      const downloadURL = await getDownloadURL(snapshot.ref);
      
      setUploadProgress(90);
      
      // Sauvegarder dans Firestore
      await addDoc(collection(db, 'galerie'), {
        title: data.title || file.name,
        album: data.album || 'general',
        imageUrl: downloadURL,
        imagePath: snapshot.ref.fullPath,
        date: serverTimestamp(),
        views: 0
      });
      
      setUploadProgress(100);
      setTimeout(() => setUploadProgress(0), 500);
      
      return { success: true };
    } catch (err) {
      console.error('Erreur upload photo:', err);
      setUploadProgress(0);
      return { success: false, error: err.message };
    }
  };

  // Supprimer une photo
  const deletePhoto = async (id, imagePath) => {
    try {
      // Supprimer du Storage
      if (imagePath) {
        try {
          const imageRef = ref(storage, imagePath);
          await deleteObject(imageRef);
        } catch (e) {
          console.warn('Image non trouvée dans Storage:', e);
        }
      }

      // Supprimer de Firestore
      const photoDoc = doc(db, 'galerie', id);
      await deleteDoc(photoDoc);
      
      return { success: true };
    } catch (err) {
      console.error('Erreur suppression photo:', err);
      return { success: false, error: err.message };
    }
  };

  return {
    photos,
    albums,
    loading,
    error,
    uploadProgress,
    uploadPhoto,
    deletePhoto
  };
};
